import { Skeleton } from "components/GlobalComponents";
import styled, { keyframes } from "styled-components";
import { bpMax } from "theme";
import { useWindowResize } from "utils/hooks";

const CardSkeleton = ({ height = "184px" }) => (
	<JobCardSkeleton height={height}>
		<JobDataPointContainer>
			<ImageSkeleton />
			<NameSkeleton />
		</JobDataPointContainer>
		<JobDataPointContainer>
			<SquareSkeleton />
			<DataPointSkeleton />
		</JobDataPointContainer>
		<JobDataPointContainer>
			<SquareSkeleton />
			<ShortDataPointSkeleton />
		</JobDataPointContainer>
		<JobDataPointContainer>
			<MidDataSkeleton />
			<MidDataSkeleton />
			<MidDataSkeleton />
			<MidDataSkeleton />
		</JobDataPointContainer>
		{height === "184px" && (
			<JobDataPointContainer>
				<SquareSkeleton />
				<MidDataSkeleton />
				<SquareSkeleton />
				<MidDataSkeleton />
			</JobDataPointContainer>
		)}
	</JobCardSkeleton>
);

const JobFeedLoadingSkeleton = () => (
	<Container>
		<JobCardSkeletonsContainer>
			<CardSkeleton />
			<CardSkeleton />
			<CardSkeleton />
			<CardSkeleton />
			<CardSkeleton />
			<CardSkeleton />
		</JobCardSkeletonsContainer>
	</Container>
);

const LoadingJobsCountTitle = () => {
	const [isMobileView] = useWindowResize();
	return (
		<Container>
			<div className="m-0 mx-[12px] flex flex-col space-y-[4px] p-0 md:mx-0">
				<Skeleton
					rounded
					borderRadius="4px"
					width={isMobileView ? "80%" : "40%"}
					height={isMobileView ? "24px" : "28px"}
					bgcolor="#e8e7ea"
				/>
				<Skeleton
					rounded
					borderRadius="4px"
					width={isMobileView ? "35%" : "20%"}
					height={isMobileView ? "16px" : "20px"}
					bgcolor="#e8e7ea"
				/>
			</div>
		</Container>
	);
};

const ModuleJobsLoadingSkeleton = () => (
	<ModuleJobsContainer>
		<CardSkeleton height="164px" />
		<CardSkeleton height="164px" />
		<CardSkeleton height="164px" />
		<CardSkeleton height="164px" />
		<CardSkeleton height="164px" />
		<CardSkeleton height="164px" />
		<CardSkeleton height="164px" />
	</ModuleJobsContainer>
);

export { JobFeedLoadingSkeleton, LoadingJobsCountTitle, ModuleJobsLoadingSkeleton };
const breatheAnimation = keyframes`
 0% { opacity: 1}
 50% {opacity: 0.55}
 100% { opacity: 1}
`;

const Container = styled.div`
	border-radius: 12px;
	background: transparent;

	${bpMax.md} {
		// padding: 1.5rem 1rem;
		overflow: scroll;
		padding-right: 0;
	}
	width: 100%;
	animation-name: ${breatheAnimation};
	animation-duration: 1.5s;
	animation-iteration-count: infinite;
`;

const ModuleJobsContainer = styled.div`
	display: flex;
	gap: 6px;
	border-radius: 12px;
	background: transparent;
	${bpMax.md} {
		padding-right: 0;
	}
	width: 100%;
	animation-name: ${breatheAnimation};
	animation-duration: 1.5s;
	animation-iteration-count: infinite;
`;

const SkeletonCommon = styled.div`
	border-radius: 4px;
	background: #e8e7ea;
`;

const JobCardSkeletonsContainer = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
	width: 100%;
	padding: 0;
	${bpMax.md} {
		align-items: center;
		padding: 16px 0 0 0;
	}
`;

const JobCardSkeleton = styled.div<{ height?: string }>`
	background: white;
	width: 100%;
	height: ${({ height }) => height || "184px"}; // Use prop value or default
	padding: 12px;
	border-radius: 8px;
	border: 1px solid #e8e7ea;
	box-shadow: 0px 2px 5px 0px rgba(0, 0, 0, 0.05);

	${bpMax.md} {
		width: 95%;
		height: ${({ height }) => height || "190px"}; // Adjust for mobile if needed
	}
`;

const JobDataPointContainer = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	margin-bottom: 12px;
	& :last-child {
		margin-bottom: 0;
	}
`;

const ImageSkeleton = styled(SkeletonCommon)`
	width: 32px;
	height: 32px;
`;
const NameSkeleton = styled(SkeletonCommon)`
	width: 97px;
	height: 24px;
	${bpMax.md} {
		width: 100%;
		height: 32px;
		flex: 1;
	}
`;
const SquareSkeleton = styled(SkeletonCommon)`
	width: 20px;
	height: 20px;
`;
const DataPointSkeleton = styled(SkeletonCommon)`
	width: 202px;
	height: 20px;
	${bpMax.md} {
		height: 13px;
	}
`;

const ShortDataPointSkeleton = styled(DataPointSkeleton)`
	width: 135px;
`;
const MidDataSkeleton = styled(DataPointSkeleton)`
	width: 100px;
	height: 20px;
`;
